import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, Button, CircularProgress, Dialog, FormHelperText, InputAdornment, MenuItem, TextField, Typography } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { apiError, isoDay } from '../format';
import { PharmacyOption } from '../types';
import { rulesApi } from './rulesApi';
import {
  AUTOCOMPLETE_SLOT_PROPS, BORDER, DARK, FIELD_SX, GHOST_BTN_SX, KIND_LABELS, KIND_ORDER, MODE_HINTS, MODE_LABELS, MUTED, PERIOD_LABELS, PRIMARY,
  PRIMARY_BTN_SX, PRIMARY_SOFT, SELECT_MENU_PROPS, SOFT, minValue, toDateInput, valueError
} from './rulesConstants';
import { DIALOG_SLOT_PROPS, DialogHeader, Segmented, modeOptions } from './RuleBits';
import { DocKind, RuleDef, RuleMode, RulePeriod, RuleRecord, RulesOverview } from './rulesTypes';
import { notify } from '../../../notifications';

interface Props {
  open: boolean;
  /** null - создание нового исключения */
  existing: RuleRecord | null;
  overview: RulesOverview;
  onClose: () => void;
  onSaved: () => void;
}

/** В исключении режим «Выключено» означает «правило для этой аптеки не действует» */
const EXCEPTION_MODE_LABELS: Record<RuleMode, string> = { ...MODE_LABELS, off: 'Не применять' };
const EXCEPTION_MODE_HINTS: Record<RuleMode, string> = { ...MODE_HINTS, off: 'Освобождение: правило для этой аптеки не действует, даже если оно включено для сети' };

const byRegionName = (a: PharmacyOption, b: PharmacyOption) => (a.region || '~').localeCompare(b.region || '~', 'ru') || a.name.localeCompare(b.name, 'ru', { numeric: true });

/** Подпись секции формы */
const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.9 }}>{children}</Typography>
);

const ExceptionEditDialog: React.FC<Props> = ({ open, existing, overview, onClose, onSaved }) => {
  const { catalog, senders, recipients } = overview;
  const editing = Boolean(existing);

  const [ruleCode, setRuleCode] = useState('');
  const [kinds, setKinds] = useState<DocKind[]>([]);
  const [from, setFrom] = useState<PharmacyOption | null>(null);
  const [to, setTo] = useState<PharmacyOption | null>(null);
  const [mode, setMode] = useState<RuleMode>('enforce');
  const [value, setValue] = useState('');
  const [period, setPeriod] = useState<RulePeriod>('day');
  const [validUntil, setValidUntil] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const def: RuleDef | undefined = catalog.rules.find(r => r.code === ruleCode);
  const sortedSenders = useMemo(() => [...senders].sort(byRegionName), [senders]);
  const sortedRecipients = useMemo(() => [...recipients].sort(byRegionName), [recipients]);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setRuleCode(existing.rule_code);
      setKinds([existing.doc_kind]);
      setFrom({ guid: existing.from_guid, name: existing.from_name || existing.from_guid, kind: 'pharmacy', region: null });
      setTo(existing.to_guid ? { guid: existing.to_guid, name: existing.to_name || existing.to_guid, kind: 'pharmacy', region: null } : null);
      setMode(existing.mode);
      setValue(String(existing.value));
      setPeriod(existing.period ?? 'day');
      setValidUntil(toDateInput(existing.valid_until));
      setNote(existing.note || '');
    } else {
      const first = catalog.rules[0];
      setRuleCode(first?.code || '');
      setKinds([]);
      setFrom(null);
      setTo(null);
      setMode('enforce');
      setValue(String(first?.defaultValue ?? ''));
      setPeriod('day');
      setValidUntil('');
      setNote('');
    }
  }, [open, existing, catalog.rules]);

  const changeRule = (code: string) => {
    setRuleCode(code);
    const next = catalog.rules.find(r => r.code === code);
    if (next) { setValue(String(next.defaultValue)); setPeriod('day'); }
    if (next?.scopeType !== 'direction') setTo(null);
  };

  const toggleKind = (k: DocKind) => setKinds(cur => (cur.includes(k) ? cur.filter(x => x !== k) : [...cur, k]));

  const needsTo = def?.scopeType === 'direction';
  const valErr = def ? valueError(def, mode, value) : null;
  const sameStock = needsTo && from && to && from.guid === to.guid;
  // прошедшую дату можно оставить только как есть (у уже истёкшего исключения): новую дату в прошлом выбрать нельзя
  const pastDate = Boolean(validUntil && validUntil < isoDay(0) && validUntil !== toDateInput(existing?.valid_until));
  const canSave = Boolean(def && from && kinds.length && (!needsTo || to) && !sameStock && !valErr && !saving && !pastDate);

  const numValue = () => Number(String(value).replace(',', '.'));

  const save = async () => {
    if (!def || !from) return;
    setSaving(true);
    try {
      const periodVal = def.periodic ? period : null;
      // у выключенного правила значение не важно - сохраняем то, что было / значение по умолчанию
      const val = mode === 'off' ? (existing ? existing.value : def.defaultValue) : numValue();
      if (existing) {
        await rulesApi.updateException(existing.id, {
          mode, value: val, period: periodVal, valid_until: validUntil || null, note: note.trim() || null
        });
        notify.success('Исключение обновлено');
      } else {
        const res = await rulesApi.createException({
          rule_code: def.code, doc_kinds: kinds, from_guid: from.guid, to_guid: needsTo ? to?.guid : undefined,
          mode, value: val, period: periodVal, valid_until: validUntil || null, note: note.trim() || null
        });
        notify.success(`Исключения сохранены: создано ${res.created}, обновлено ${res.updated}`);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      notify.error(apiError(err, 'Не удалось сохранить исключение'));
    } finally {
      setSaving(false);
    }
  };

  const pharmacyField = (label: string, val: PharmacyOption | null, set: (v: PharmacyOption | null) => void, options: PharmacyOption[], disabled?: boolean) => (
    <Autocomplete
      size="small" options={options} value={val} onChange={(_, v) => set(v)} disabled={disabled || saving}
      getOptionLabel={o => o.name} isOptionEqualToValue={(a, b) => a.guid === b.guid}
      groupBy={o => o.region || 'Без региона'} noOptionsText="Ничего не найдено"
      renderInput={params => <TextField {...params} label={label} sx={FIELD_SX} />}
      slotProps={AUTOCOMPLETE_SLOT_PROPS}
    />
  );

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" slotProps={DIALOG_SLOT_PROPS}>
      <DialogHeader
        title={editing ? 'Исключение' : 'Новое исключение'}
        subtitle={editing ? 'Измените режим, значение или срок действия' : 'Другое значение правила для конкретной аптеки или направления'}
        onClose={saving ? undefined : onClose}
      />
      <Box sx={{ px: 3, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5, overflowY: 'auto', bgcolor: SOFT }}>
        <Box>
          <SectionLabel>Что ограничиваем</SectionLabel>
          <TextField
            select size="small" fullWidth value={ruleCode} onChange={e => changeRule(e.target.value)} disabled={editing || saving} sx={FIELD_SX}
            SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
          >
            {catalog.rules.map(r => <MenuItem key={r.code} value={r.code}>{r.name}</MenuItem>)}
          </TextField>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.25 }}>
            {KIND_ORDER.map(k => {
              const sel = kinds.includes(k);
              const locked = editing || saving;
              return (
                <Box
                  key={k} component="button" type="button" role="checkbox" aria-checked={sel} onClick={() => { if (!locked) toggleKind(k); }}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 1.5, py: 0.7, borderRadius: 99, fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                    border: `1px solid ${sel ? PRIMARY : BORDER}`, bgcolor: sel ? PRIMARY_SOFT : '#fff', color: sel ? PRIMARY : MUTED,
                    cursor: locked ? 'default' : 'pointer', opacity: locked && !sel ? 0.45 : 1, transition: 'all .15s',
                    '&:hover': locked ? {} : { borderColor: PRIMARY, color: PRIMARY },
                    '&:focus-visible': { outline: `2px solid ${PRIMARY}`, outlineOffset: 1 }
                  }}
                >
                  {sel && <CheckRoundedIcon sx={{ fontSize: 15 }} />}
                  {KIND_LABELS[k]}
                </Box>
              );
            })}
          </Box>
          {!editing && <FormHelperText sx={{ mx: 0.5 }}>Вид документа - можно выбрать несколько</FormHelperText>}
        </Box>

        <Box>
          <SectionLabel>{needsTo ? 'Направление' : 'Аптека'}</SectionLabel>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {pharmacyField('Аптека-отправитель', from, setFrom, sortedSenders, editing)}
            {needsTo && pharmacyField('Аптека-получатель', to, setTo, sortedRecipients, editing)}
          </Box>
          {sameStock && <FormHelperText error sx={{ mx: 0.5 }}>Отправитель и получатель должны различаться</FormHelperText>}
          {!needsTo && def && (
            <FormHelperText sx={{ mx: 0.5 }}>Исключение действует на все документы выбранного вида, которые создаёт эта аптека.</FormHelperText>
          )}
        </Box>

        <Box>
          <SectionLabel>Как проверяем</SectionLabel>
          <Segmented
            value={mode} onChange={setMode} disabled={saving}
            options={modeOptions(catalog.modes.map(m => m.id), EXCEPTION_MODE_LABELS, EXCEPTION_MODE_HINTS)}
          />
          <FormHelperText sx={{ mx: 0.5 }}>{EXCEPTION_MODE_HINTS[mode]}</FormHelperText>
          {mode !== 'off' && def && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap', mt: 1.5 }}>
              <TextField
                size="small" type="number" label="Значение" value={value} onChange={e => setValue(e.target.value)} disabled={saving}
                error={Boolean(valErr)} helperText={valErr || (def.periodic && Number(value) === 0 ? 'Направление закрыто' : undefined)}
                inputProps={{ min: minValue(def), step: def.integer ? 1 : 100 }}
                InputProps={{ endAdornment: <InputAdornment position="end"><Box component="span" sx={{ fontSize: 12.5, color: MUTED }}>{def.unit}</Box></InputAdornment> }}
                sx={{ width: 170, ...FIELD_SX }}
              />
              {def.periodic && (
                <Box sx={{ minHeight: 40, display: 'flex', alignItems: 'center' }}>
                  <Segmented
                    small value={period} onChange={setPeriod} disabled={saving}
                    options={catalog.periods.map(p => ({ value: p.id, label: PERIOD_LABELS[p.id] }))}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box>
          <SectionLabel>Срок и примечание</SectionLabel>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField
              size="small" type="date" label="Действует до (включительно)" value={validUntil} onChange={e => setValidUntil(e.target.value)} disabled={saving}
              InputLabelProps={{ shrink: true }} sx={{ maxWidth: 260, ...FIELD_SX }}
              error={pastDate}
              helperText={pastDate ? 'Дата уже прошла' : 'Пусто - бессрочно. После этой даты действует правило сети'}
              FormHelperTextProps={{ sx: { mx: 0.5, whiteSpace: 'normal', width: 380, maxWidth: '90vw' } }}
            />
            <TextField
              size="small" label="Примечание" value={note} onChange={e => setNote(e.target.value)} disabled={saving} multiline minRows={2}
              inputProps={{ maxLength: 300 }} sx={FIELD_SX}
            />
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 3, py: 1.75, borderTop: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
        <Button onClick={onClose} disabled={saving} sx={GHOST_BTN_SX}>Отмена</Button>
        <Button
          variant="contained" disableElevation onClick={save} disabled={!canSave} sx={PRIMARY_BTN_SX}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          Сохранить
        </Button>
      </Box>
    </Dialog>
  );
};

export default ExceptionEditDialog;
